<script setup lang="ts">
import { useVuelidate } from "@vuelidate/core";
import { useValidators } from "@/composables/useValidators";
import CnmcFieldErrors from "./CnmcFieldErrors.vue";
import CnmcProductionRow from "./CnmcProductionRow.vue";
import CnmcDialog from "./CnmcDialog.vue";
import { isEqual, sumBy } from "lodash-es";
import { computed, ref, toRaw, useId, useTemplateRef, watch } from "vue";
import { useI18n } from "vue-i18n";
import WizardStepRail from "@/components/common/WizardStepRail.vue";
import WizardSection from "@/components/common/WizardSection.vue";
import WizardFooter from "@/components/common/WizardFooter.vue";
import InfoCallout from "@/components/common/InfoCallout.vue";
import CnmcAllocationSummary from "./CnmcAllocationSummary.vue";
import CnmcDestinationEditor from "./CnmcDestinationEditor.vue";
import { cnmcApi } from "./cnmcApi";
import type {
  CnmcDevice,
  CnmcContext,
  CnmcFilingProfile,
  CnmcPlan,
  CnmcProductionInput,
  CnmcRecipient,
} from "./cnmcTypes";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { translateCountryCode } from "@/utils/countries";

const props = defineProps<{
  companyId: number;
  connectionId: number;
  profile: CnmcFilingProfile;
  context: CnmcContext;
  devices: CnmcDevice[];
  canImport: boolean;
  existing?: CnmcPlan;
}>();
const emit = defineEmits<{
  saved: [plan: CnmcPlan];
  cancel: [];
  filingDetails: [];
}>();
const { globalErrorHandler } = useGlobalHandlers();
const { t, te } = useI18n();
const id = useId();
const visible = ref(true);
const steps = computed(() =>
  ["production", "allocation", "review"].map((key) => ({
    key,
    title: t(`cnmc.${key}`),
  }))
);
const step = ref(0);
const busy = ref(false);
watch(
  () => props.profile,
  () => {
    step.value = 0;
  }
);
const preview = ref<CnmcPlan | null>(null);
const initialDestination = props.existing
  ? props.existing.inputs.market_destination
  : props.context.default_market_destination;
const marketDestination = ref<CnmcRecipient>(
  structuredClone(
    toRaw(initialDestination ?? { name: "", region: "", eecs: "" })
  )
);
const editingDestination = ref(false);
const destination = computed(() =>
  marketDestination.value.eecs || marketDestination.value.name
    ? marketDestination.value
    : null
);
const marketGoCount = computed(() =>
  preview.value
    ? preview.value.total_go_count -
      sumBy(
        preview.value.allocations.filter((row) => row.purpose === "PPA"),
        "go_count"
      )
    : 0
);
type ProductionRow = CnmcProductionInput & { uid: string };
const withRowId = (row: CnmcProductionInput): ProductionRow => ({
  ...row,
  uid: crypto.randomUUID(),
});
const production = ref<ProductionRow[]>(
  props.existing?.inputs.production_sources.map(
    ({ registry_device_id, production_data_id, month, value }) =>
      withRowId({
        registry_device_id,
        production_data_id,
        month,
        value,
      })
  ) ?? []
);
const validators = useValidators(t);
const rules = computed(() => ({
  production:
    step.value === 0 ? { required: validators.selectionRequired } : {},
}));
const v$ = useVuelidate(rules, { production }, { $stopPropagation: true });
const fileInput = useTemplateRef<HTMLInputElement>("fileInput");
const uploading = ref(false);
const uploadedFileName = ref("");
const emptyMonth = new Date(
  new Date().getFullYear(),
  new Date().getMonth() - 1,
  15
)
  .toISOString()
  .slice(0, 7);
const total = computed(() =>
  production.value.reduce(
    (sum, row) => sum + Math.floor(Number(row.value || 0)),
    0
  )
);
const addRow = () =>
  production.value.push(
    withRowId({
      registry_device_id:
        props.devices.find((device) => !device.preparation_error)?.id ?? 0,
      month: `${emptyMonth}-01`,
      value: "",
    })
  );
const uploadReadings = async (event: Event) => {
  const input = event.target as HTMLInputElement;
  const file = input.files?.[0];
  input.value = "";
  if (!file || busy.value || !props.canImport) return;
  if (
    !/\.(xlsx?|csv)$/i.test(file.name) ||
    !file.size ||
    file.size > 25 * 1_048_576
  ) {
    globalErrorHandler(t("cnmc.invalid_readings_file"));
    return;
  }
  busy.value = true;
  uploading.value = true;
  uploadedFileName.value = "";
  try {
    const { data } = await cnmcApi.uploadReadings(
      props.companyId,
      props.connectionId,
      file
    );
    const key = (row: CnmcProductionInput) =>
      `${row.registry_device_id}:${row.month}`;
    const existing = new Map(production.value.map((row) => [key(row), row]));
    const conflict = data.readings.some((row) => {
      const current = existing.get(key(row));
      return (
        current &&
        current.value !== "" &&
        Number(current.value) !== Number(row.value)
      );
    });
    if (conflict) {
      globalErrorHandler(t("cnmc.upload_readings_conflict"));
      return;
    }
    const uploaded = new Map(data.readings.map((row) => [key(row), row]));
    production.value = [
      ...production.value.map((row) =>
        row.value === ""
          ? { ...(uploaded.get(key(row)) ?? row), uid: row.uid }
          : row
      ),
      ...data.readings.filter((row) => !existing.has(key(row))).map(withRowId),
    ];
    uploadedFileName.value = file.name;
  } catch (error) {
    globalErrorHandler(error);
  } finally {
    busy.value = false;
    uploading.value = false;
  }
};
const next = async () => {
  v$.value.$touch();
  if (busy.value) return;
  if (v$.value.$invalid) {
    if (step.value === 1) editingDestination.value = true;
    return;
  }
  if (step.value < 2) {
    busy.value = true;
    try {
      if (
        step.value === 0 ||
        !isEqual(
          destination.value,
          preview.value?.inputs.market_destination ?? null
        )
      ) {
        const response = await cnmcApi.save(
          props.companyId,
          props.connectionId,
          {
            production: production.value.map(({ uid: _uid, ...row }) => row),
            filing_details: props.profile,
            market_destination: destination.value,
            replaces_plan_id:
              props.existing?.status !== "DRAFT"
                ? props.existing?.id
                : undefined,
          },
          preview.value?.id ??
            (props.existing?.status === "DRAFT" ? props.existing.id : undefined)
        );
        preview.value = response.data;
      }
      step.value++;
    } catch (error) {
      globalErrorHandler(error);
    } finally {
      busy.value = false;
    }
  } else if (preview.value) emit("saved", preview.value);
};
</script>

<template>
  <CnmcDialog
    v-model="visible"
    :title="$t('cnmc.prepare_issuance')"
    :eyebrow="`${$t('tradingCenter.tabs.issuance_reporting.title')} · CNMC`"
    :busy
    wide
    @close="emit('cancel')"
  >
    <WizardStepRail
      :steps
      :currentIndex="step"
      :stepLabel="$t('common.step')"
      :ariaLabel="$t('cnmc.progress')"
    />
    <form :id="`${id}-form`" novalidate @submit.prevent="next">
      <WizardSection
        v-if="step === 0"
        :title="$t('cnmc.net_production')"
        :description="$t('cnmc.monthly_breakdown')"
      >
        <div class="d-flex flex-wrap gap-2 mb-3">
          <button
            type="button"
            class="btn btn-outline-primary btn-sm"
            :disabled="!devices.length || busy"
            @click="addRow"
          >
            {{ $t("cnmc.enter_manually") }}
          </button>
          <button
            type="button"
            class="btn btn-outline-secondary btn-sm"
            :disabled="busy || !canImport || !devices.length"
            :aria-describedby="`${id}-upload-help`"
            @click="fileInput?.click()"
          >
            {{ $t("cnmc.upload_with_ai") }}
          </button>
          <input
            ref="fileInput"
            type="file"
            class="d-none"
            accept=".xlsx,.xls,.csv"
            :disabled="busy || !canImport"
            :aria-label="$t('cnmc.upload_with_ai')"
            @change="uploadReadings"
          />
        </div>
        <p :id="`${id}-upload-help`" class="small text-body-secondary mb-3">
          {{
            canImport
              ? $t("cnmc.upload_readings_help")
              : $t("issuance_reporting.import_not_trusted")
          }}
        </p>
        <p v-if="uploading" class="small mb-3" role="status">
          <span
            class="spinner-border spinner-border-sm me-2"
            aria-hidden="true"
          />
          {{ $t("issuance_reporting.file_upload.analyzing") }}
        </p>
        <p v-else-if="uploadedFileName" class="small mb-3" role="status">
          {{ $t("cnmc.readings_uploaded", { filename: uploadedFileName }) }}
        </p>
        <div class="table-responsive">
          <table
            class="table table-padded align-middle mb-2 cnmc-production-table"
          >
            <thead class="table-light">
              <tr>
                <th>{{ $t("cnmc.device") }}</th>
                <th>{{ $t("cnmc.month") }}</th>
                <th>{{ $t("cnmc.production_mwh") }}</th>
                <th>
                  <span class="visually-hidden">{{ $t("common.remove") }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <CnmcProductionRow
                v-for="(row, index) in production"
                :key="row.uid"
                :model-value="row"
                @update:model-value="
                  production[index] = { ...$event, uid: row.uid }
                "
                :devices
                :busy
                @remove="production.splice(index, 1)"
              />
              <tr v-if="!production.length">
                <td
                  colspan="4"
                  class="text-body-secondary small py-4 text-center"
                >
                  {{ $t("cnmc.add_production_help") }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <CnmcFieldErrors :validation="v$.production" />
        <p class="small fw-medium mt-3 mb-0">
          {{ $t("cnmc.whole_go_total", { count: total }) }}
        </p>
        <fieldset v-if="!initialDestination" :disabled="busy" class="mt-4">
          <legend class="h5">{{ $t("cnmc.market_destination") }}</legend>
          <CnmcDestinationEditor
            v-model="marketDestination"
            :destinations="context.destinations"
            :required="false"
          />
        </fieldset>
      </WizardSection>
      <template v-else-if="step === 1 && preview">
        <WizardSection
          :title="$t('cnmc.ppa_reserves')"
          :description="$t('cnmc.allocation_explanation')"
        >
          <div class="table-responsive">
            <table class="table table-padded align-middle mb-0">
              <thead class="table-light">
                <tr>
                  <th>{{ $t("cnmc.recipient_name") }}</th>
                  <th>{{ $t("cnmc.month") }}</th>
                  <th>{{ $t("cnmc.route") }}</th>
                  <th>GOs</th>
                  <th>{{ $t("cnmc.delivery_due") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="allocation in preview.allocations.filter(
                    (row) => row.purpose === 'PPA'
                  )"
                  :key="allocation.id"
                >
                  <td>
                    <strong>{{ allocation.recipient_snapshot.name }}</strong>
                    <div class="small text-body-secondary">
                      PPA #{{ allocation.ppa_portfolio_id }}
                    </div>
                  </td>
                  <td>{{ allocation.production_month.slice(0, 7) }}</td>
                  <td>{{ $t(`cnmc.routes.${allocation.route}`) }}</td>
                  <td>{{ $n(allocation.go_count) }}</td>
                  <td>{{ $t("cnmc.awaiting_issuance_receipt") }}</td>
                </tr>
                <tr
                  v-if="
                    !preview.allocations.some((row) => row.purpose === 'PPA')
                  "
                >
                  <td colspan="5" class="text-body-secondary py-3">
                    {{ $t("cnmc.no_ppa_reserves") }}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </WizardSection>
        <WizardSection
          v-if="marketGoCount"
          :title="$t('cnmc.market_destination')"
        >
          <fieldset :disabled="busy">
            <div class="d-flex flex-wrap align-items-baseline gap-2 mb-2">
              <strong>{{ marketDestination.name }}</strong>
              <span class="small text-body-secondary">
                {{ translateCountryCode(marketDestination.region, t, te) }} ·
                {{ marketDestination.eecs }}
              </span>
            </div>
            <details
              :open="editingDestination"
              @toggle="
                editingDestination = ($event.target as HTMLDetailsElement).open
              "
            >
              <summary class="small text-primary">
                {{ $t("cnmc.change_destination") }}
              </summary>
              <div class="mt-3">
                <CnmcDestinationEditor
                  v-model="marketDestination"
                  :destinations="context.destinations"
                />
              </div>
            </details>
          </fieldset>
        </WizardSection>
        <div class="p-3 p-md-4"><CnmcAllocationSummary :plan="preview" /></div>
      </template>
      <template v-else-if="preview">
        <WizardSection
          v-if="marketGoCount && preview.inputs.market_destination"
          :title="$t('cnmc.market_destination')"
        >
          <strong>{{ preview.inputs.market_destination.name }}</strong>
          <div class="small text-body-secondary mt-1">
            {{ preview.inputs.market_destination.region }} ·
            {{ preview.inputs.market_destination.eecs }} ·
            {{ $n(marketGoCount) }} GOs
          </div>
        </WizardSection>
        <WizardSection
          :title="$t('cnmc.filing_details')"
          :description="$t('cnmc.filing_snapshot_help')"
        >
          <div class="d-flex justify-content-between flex-wrap gap-2">
            <div>
              <strong>{{ preview.inputs.filing_details.producer_name }}</strong>
              <div class="small text-body-secondary mt-1">
                {{ preview.inputs.filing_details.email }} ·
                {{ preview.inputs.filing_details.phone }}
              </div>
            </div>
            <button
              type="button"
              class="btn btn-outline-secondary btn-sm align-self-start"
              @click="emit('filingDetails')"
            >
              {{ $t("cnmc.filing_details") }}
            </button>
          </div>
        </WizardSection>
        <WizardSection
          :title="$t('cnmc.issuance_stage')"
          :description="$t('cnmc.saved_draft_explanation')"
        >
          <div class="d-flex flex-column gap-2">
            <div
              v-for="route in [
                { key: 'DOMESTIC_ISSUANCE', count: preview.domestic_go_count },
                { key: 'EXPORT_ISSUANCE', count: preview.export_go_count },
              ].filter((item) => item.count)"
              :key="route.key"
              class="border rounded-3 p-3 d-flex justify-content-between align-items-center gap-3"
            >
              <div>
                <strong class="small">{{
                  $t(`cnmc.kinds.${route.key}`)
                }}</strong>
                <div class="small text-body-secondary mt-1">
                  Expedicion.xlsm
                </div>
              </div>
              <span class="fw-semibold text-nowrap"
                >{{ $n(route.count) }} GOs</span
              >
            </div>
          </div>
          <InfoCallout icon="file-lines" class="mt-3">{{
            $t("cnmc.download_notice")
          }}</InfoCallout>
        </WizardSection>
      </template>
    </form>
    <template #footer>
      <WizardFooter class="w-100">
        <template #start>
          <button
            v-if="step > 0"
            type="button"
            class="btn btn-link btn-sm p-0"
            :disabled="busy"
            @click="step--"
          >
            {{ $t("common.back") }}
          </button>
          <button
            v-else
            type="button"
            class="btn btn-link btn-sm p-0"
            :disabled="busy"
            @click="emit('filingDetails')"
          >
            {{ $t("cnmc.filing_details") }}
          </button>
        </template>
        <template #end>
          <span class="small text-body-secondary d-none d-sm-inline"
            >{{ $t("common.step") }} {{ step + 1 }} / 3</span
          >
          <button
            type="button"
            class="btn btn-link"
            :disabled="busy"
            @click="emit('cancel')"
          >
            {{ $t("common.cancel") }}
          </button>
          <button
            :form="`${id}-form`"
            type="submit"
            class="btn btn-primary"
            :disabled="busy || !production.length"
          >
            {{ $t(step === 2 ? "cnmc.open_batch" : "common.continue") }}
          </button>
        </template>
      </WizardFooter>
    </template>
  </CnmcDialog>
</template>

<style scoped lang="scss">
.cnmc-production-table {
  min-width: 560px;
}
</style>
